import {
  ArrowRight,
  CalendarDays,
  Check,
  Filter,
  Inbox,
  Layers3,
  Search,
  Sparkles,
} from "lucide-react";
import { SignInButton, SignUpButton } from "@clerk/react";
import heroImage from "../../assets/life-workspace.jpg";
import brandIcon from "../../assets/brand-icon.png";

const mailRows = [
  { sender: "Northwind Studio", subject: "Project timeline", time: "09:42", color: "#c7e0f4" },
  { sender: "Alex Morgan", subject: "Dinner on Thursday?", time: "08:18", color: "#d8ead2" },
  { sender: "Contoso Billing", subject: "Your July receipt", time: "Yesterday", color: "#fde2c5" },
  { sender: "Maya Chen", subject: "Weekend plans", time: "Yesterday", color: "#f5d5e0" },
];

function BrandMark() {
  return (
    <span
      className="grid size-8 place-items-center overflow-hidden rounded-md bg-white"
      aria-hidden="true"
    >
      <img src={brandIcon} alt="" className="size-full object-cover" />
    </span>
  );
}

function ProductPreview() {
  return (
    <div className="landing-product" aria-label="Preview of the mysmall.life workspace">
      <div className="landing-product-bar">
        <div className="flex items-center gap-2.5">
          <BrandMark />
          <span className="text-[13px] font-semibold text-white">mysmall.life</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="size-2 rounded-full bg-white/35" />
          <span className="size-6 rounded-full bg-[#f5d5e0]" />
        </div>
      </div>
      <div className="landing-product-tabs">
        <span>Dashboard</span>
        <span className="is-active"><Layers3 size={13} /> All accounts</span>
        <span className="account-tab"><i className="bg-[#0078d4]">1</i> Work</span>
        <span className="account-tab"><i className="bg-[#34a853]">2</i> Personal</span>
      </div>
      <div className="landing-product-body">
        <aside className="landing-product-nav">
          <p>Filters</p>
          <span className="filter-chip"><Sparkles size={11} /> Important</span>
          <span className="filter-chip muted"><Filter size={11} /> Receipts</span>
          <p>Folders</p>
          <span className="nav-active"><Inbox size={14} /> Inbox <b>12</b></span>
          <span><CalendarDays size={14} /> Calendar</span>
          <span><Search size={14} /> Search</span>
        </aside>
        <div className="landing-mail-list">
          <div className="landing-pane-heading">
            <strong>Today</strong>
            <span>All caught up in one place</span>
          </div>
          {mailRows.map((mail, index) => (
            <div className={`landing-mail-row ${index === 0 ? "is-selected" : ""}`} key={mail.sender}>
              <span className="landing-avatar" style={{ backgroundColor: mail.color }}>
                {mail.sender[0]}
              </span>
              <span className="min-w-0 flex-1">
                <b>{mail.sender}</b>
                <small>{mail.subject}</small>
              </span>
              <time>{mail.time}</time>
            </div>
          ))}
        </div>
        <div className="landing-calendar">
          <div className="landing-pane-heading">
            <strong>Thursday, July 11</strong>
            <span>3 events</span>
          </div>
          <div className="calendar-grid">
            <span className="hour h1">9</span><span className="hour h2">10</span>
            <span className="hour h3">11</span><span className="hour h4">12</span>
            <div className="event event-blue"><b>Weekly focus</b><small>09:30 - 10:15</small></div>
            <div className="event event-green"><b>Lunch with Maya</b><small>11:30 - 12:30</small></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AuthActions({ inverse = false }: { inverse?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <SignUpButton mode="modal">
        <button className={inverse ? "landing-cta-light" : "landing-cta-primary"}>
          Start organizing <ArrowRight size={16} />
        </button>
      </SignUpButton>
      <SignInButton mode="modal">
        <button className={inverse ? "landing-cta-ghost-light" : "landing-cta-secondary"}>
          Log in
        </button>
      </SignInButton>
    </div>
  );
}

const capabilities = [
  {
    icon: Inbox,
    label: "One inbox, every account",
    copy: "Bring Microsoft and Google mail together, then move between a combined view and each account without losing context.",
  },
  {
    icon: CalendarDays,
    label: "A calendar that sees the whole day",
    copy: "See personal and work commitments in one schedule, with clear account colors and the view that fits your week.",
  },
  {
    icon: Sparkles,
    label: "Less sorting, more living",
    copy: "Use focused search and optional AI filters to surface what matters and quiet the rest of the inbox.",
  },
];

export function LandingPage() {
  return (
    <div className="landing-page">
      <header className="landing-header">
        <a href="#top" className="flex items-center gap-2.5" aria-label="mysmall.life home">
          <BrandMark />
          <span className="text-[16px] font-bold text-[#17202a]">mysmall.life</span>
        </a>
        <nav className="hidden items-center gap-7 md:flex" aria-label="Main navigation">
          <a href="#product">Product</a>
          <a href="#how-it-works">How it works</a>
          <a href="#vision">Our vision</a>
        </nav>
        <div className="flex items-center gap-2">
          <SignInButton mode="modal">
            <button className="landing-nav-login">Log in</button>
          </SignInButton>
          <SignUpButton mode="modal">
            <button className="landing-nav-signup">Sign up</button>
          </SignUpButton>
        </div>
      </header>

      <main>
        <section id="top" className="landing-hero">
          <img src={heroImage} alt="An organized desk with email and calendar visible" />
          <div className="landing-hero-copy">
            <p className="landing-eyebrow">Your day, finally in one place</p>
            <h1>mysmall.life</h1>
            <p className="landing-hero-lead">
              A calmer way to manage the moving parts of your life. Start with every inbox and
              calendar together, so you can see what needs you and get back to what matters.
            </p>
            <AuthActions />
            <p className="landing-provider-note">
              <Check size={14} /> Connect Microsoft and Google accounts after signup
            </p>
          </div>
        </section>

        <section id="product" className="landing-section landing-product-section">
          <div className="landing-section-heading">
            <p className="landing-eyebrow">A clear view of now</p>
            <h2>Your digital life should feel smaller.</h2>
            <p>
              Stop checking separate tabs to understand your own day. mysmall.life brings the
              essentials into one quiet workspace built for scanning, deciding, and moving on.
            </p>
          </div>
          <ProductPreview />
        </section>

        <section className="landing-capabilities" aria-label="Product capabilities">
          {capabilities.map(({ icon: Icon, label, copy }, index) => (
            <article key={label}>
              <span className={`capability-icon capability-${index + 1}`}><Icon size={22} /></span>
              <h3>{label}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </section>

        <section id="how-it-works" className="landing-section landing-steps-section">
          <div className="landing-section-heading compact">
            <p className="landing-eyebrow">Simple from the start</p>
            <h2>From scattered to settled in minutes.</h2>
          </div>
          <ol className="landing-steps">
            <li><span>01</span><div><h3>Create your space</h3><p>Make one secure mysmall.life account for your personal command center.</p></div></li>
            <li><span>02</span><div><h3>Connect your accounts</h3><p>Add the Microsoft and Google accounts that already hold your days.</p></div></li>
            <li><span>03</span><div><h3>See the whole picture</h3><p>Move through combined mail, focused inboxes, and a unified calendar.</p></div></li>
          </ol>
        </section>

        <section id="vision" className="landing-vision">
          <div>
            <p className="landing-eyebrow light">Built around a bigger idea</p>
            <h2>Email and calendar are only the beginning.</h2>
            <p>
              Life rarely arrives in neat categories. mysmall.life begins where much of it already
              lives, then grows toward one thoughtful home for the plans, responsibilities, and
              everyday details that make up your world.
            </p>
          </div>
          <div className="vision-list">
            <span><Check size={16} /> Available now: unified email</span>
            <span><Check size={16} /> Available now: combined calendar</span>
            <span><ArrowRight size={16} /> Designed to grow with your life</span>
          </div>
        </section>

        <section className="landing-final-cta">
          <p className="landing-eyebrow">Make room for the rest of your day</p>
          <h2>Bring your small life together.</h2>
          <p>One account. Every inbox and calendar. A clearer place to begin.</p>
          <AuthActions inverse />
        </section>
      </main>

      <footer className="landing-footer">
        <div className="flex items-center gap-2"><BrandMark /><b>mysmall.life</b></div>
        <p>Email and calendar, together today. More of life, thoughtfully organized tomorrow.</p>
        <SignInButton mode="modal"><button>Log in</button></SignInButton>
      </footer>
    </div>
  );
}
